import React, { useState, useCallback, useRef } from 'react';
import { Upload, File, X, Loader2, AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import {
  validateFile,
  getFileInfo,
  calculateProcessingTime,
  getProcessingStages,
  getAcceptAttribute
} from '../utils/documentFormats';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

// Fonction utilitaire pour le téléchargement avec suivi de progression
const uploadWithProgress = (url, formData, token, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startTime = Date.now();
    let lastLoaded = 0;
    let lastPercent = 0;
    
    // Suivi de la progression avec garantie de monotonie
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        // Calculer le pourcentage
        const rawPercent = (event.loaded / event.total) * 100;
        
        // Garantir la monotonie : ne jamais revenir en arrière
        const percentComplete = Math.max(lastPercent, Math.min(100, Math.round(rawPercent)));
        lastPercent = percentComplete;
        
        // Calculer la vitesse uniquement si on a progressé
        let speed = 0;
        if (event.loaded > lastLoaded) {
          const timeElapsed = Date.now() - startTime;
          if (timeElapsed > 0) {
            speed = (event.loaded - lastLoaded) / (timeElapsed / 1000);
          }
          lastLoaded = event.loaded;
        }
        
        onProgress(percentComplete, speed);
      }
    });

    // Gestion de la réponse
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject(new Error('Réponse invalide du serveur'));
        }
      } else {
        reject(new Error(`Erreur HTTP: ${xhr.status}`));
      }
    });

    // Gestion des erreurs
    xhr.addEventListener('error', () => {
      reject(new Error('Erreur réseau lors du téléchargement'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Téléchargement annulé'));
    });

    // Ouvrir et envoyer la requête
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
};

// Fonction pour obtenir le message correspondant à une progression
const getStatusMessage = (progress, processingSteps) => {
  for (let i = processingSteps.length - 1; i >= 0; i--) {
    if (progress >= processingSteps[i].progress) {
      return processingSteps[i].message;
    }
  }
  return "Préparation...";
};

const UploadZone = ({ onFileSelect, isProcessing, disabled, selectedFile, uploadProgress, uploadStatus, fileInfo }) => (
  <div
    className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors
      ${!disabled && !isProcessing ? 'cursor-pointer hover:bg-gray-50' : 'opacity-75 cursor-not-allowed'}`}
    onClick={() => !disabled && !isProcessing && document.getElementById('file-upload').click()}
  >
    <input
      id="file-upload"
      type="file"
      accept={getAcceptAttribute()}
      onChange={onFileSelect}
      className="hidden"
      disabled={disabled || isProcessing}
    />

    {!isProcessing ? (
      <>
        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          {selectedFile ? `Fichier sélectionné : ${selectedFile.name}` : 'Déposez votre document ici'}
        </h3>
        {selectedFile && fileInfo ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl">{fileInfo.icon}</span>
              <span
                className="px-3 py-1 text-sm rounded-full font-medium"
                style={{
                  backgroundColor: `${fileInfo.color}20`,
                  color: fileInfo.color
                }}
              >
                {fileInfo.type}
              </span>
              {fileInfo.hasMultimodal && (
                <span className="px-3 py-1 text-sm bg-purple-50 text-purple-700 rounded-full font-medium">
                  ✨ Analyse Multimodale
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm">
              Taille : {fileInfo.sizeFormatted}
            </p>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            Formats acceptés: PDF, Word, PowerPoint, Excel, Images (JPG, PNG, GIF, WebP, BMP, TIFF), Texte (TXT, CSV, Markdown, RTF)
          </p>
        )}
        {selectedFile && (
          <p className="text-blue-600 text-sm mt-2">
            Cliquez à nouveau pour sélectionner un autre fichier
          </p>
        )}
      </>
    ) : (
      <div className="space-y-4">
        {/* Barre de progression */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
        
        {/* Informations de progression */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {uploadProgress < 100 ? (
                <Loader2 size={16} className="text-blue-600 animate-spin" />
              ) : (
                <CheckCircle size={16} className="text-green-600" />
              )}
              <span className={`text-sm font-medium ${
                uploadProgress === 100 ? 'text-green-700' : 'text-gray-700'
              }`}>
                {uploadStatus}
              </span>
            </div>
            
            <span className="text-sm text-gray-600">
              {uploadProgress}%
            </span>
          </div>
        </div>
        
        <p className="text-xs text-gray-500">
          Veuillez patienter, cette opération peut prendre quelques instants
        </p>
      </div>
    )}
  </div>
);

const ErrorDisplay = ({ error, onRetry, onClose }) => {
  // Déterminer le type d'erreur pour afficher un message approprié
  const getErrorMessage = (errorMsg) => {
    if (errorMsg.includes('trop volumineux')) {
      return {
        title: 'Fichier trop volumineux',
        message: 'Le fichier dépasse la limite de 50MB. Veuillez sélectionner un fichier plus petit.',
        suggestion: 'Compressez le fichier ou divisez-le en plusieurs parties.'
      };
    }
    if (errorMsg.includes('DUPLICATE_UPLOAD')) {
      return {
        title: 'Fichier déjà en cours de traitement',
        message: 'Ce fichier est déjà en cours d\'upload. Veuillez attendre la fin du traitement.',
        suggestion: 'Attendez quelques instants ou vérifiez vos documents existants.'
      };
    }
    if (errorMsg.includes('network') || errorMsg.includes('Network')) {
      return {
        title: 'Problème de connexion',
        message: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
        suggestion: 'Vérifiez votre connexion et réessayez.'
      };
    }
    return {
      title: 'Erreur lors de l\'upload',
      message: errorMsg,
      suggestion: 'Vérifiez le format du fichier et réessayez.'
    };
  };

  const errorInfo = getErrorMessage(error);

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-start">
        <AlertCircle className="h-5 w-5 mr-3 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-red-800 mb-1">{errorInfo.title}</h4>
          <p className="text-red-700 text-sm mb-3">{errorInfo.message}</p>
          <p className="text-red-600 text-xs mb-4">{errorInfo.suggestion}</p>
          
          <div className="flex gap-2">
            {onRetry && (
              <button
                onClick={onRetry}
                className="flex items-center px-3 py-1.5 bg-red-100 text-red-700 border border-red-300 rounded-md hover:bg-red-200 transition-colors text-sm"
              >
                <RefreshCw className="w-3 h-3 mr-1.5" />
                Réessayer
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors text-sm"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentView = ({ document, onClose }) => {
  const [showFullSummary, setShowFullSummary] = useState(false);

  const summary = document.analysis?.summary || "Aucun résumé disponible";
  const displayedSummary = showFullSummary ? summary : summary.slice(0, 300);
  const shouldTruncate = summary.length > 300;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-semibold text-gray-800">
              {document.metadata?.title || document.fileName.replace(/\.[^/.]+$/, "") || "Document sans titre"}
            </h3>
            <span className="text-sm text-gray-500">
              {new Date(document.metadata?.uploadedAt).toLocaleDateString()}
            </span>
          </div>

          <div className="prose prose-sm max-w-none">
            <p className="text-gray-600">
              {displayedSummary}
              {shouldTruncate && !showFullSummary && '...'}
            </p>
            {shouldTruncate && (
              <button
                onClick={() => setShowFullSummary(!showFullSummary)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2"
              >
                {showFullSummary ? 'Voir moins' : 'Voir plus'}
              </button>
            )}
          </div>

          {document.metadata?.documentType && (
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full">
                {document.metadata.documentType}
              </span>
              {document.metadata?.pageCount && (
                <span className="px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded-full">
                  {document.metadata.pageCount} pages
                </span>
              )}
            </div>
          )}

          {document.analysis?.concepts?.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-700 mb-3">Concepts clés</h4>
              <div className="flex flex-wrap gap-2">
                {document.analysis.concepts.map((concept, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 rounded-full"
                  >
                    {typeof concept === 'object' ? concept.title : concept}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <a 
              href={document.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <File className="w-4 h-4 mr-2" />
              Voir le document
            </a>
            <button 
              onClick={onClose}
              className="flex items-center px-4 py-2 border border-gray-200 text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadDocument = ({ competence, userId, onUploadComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [document, setDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [processingSteps, setProcessingSteps] = useState([]);

  const auth = getAuth();
  const lastProgressRef = useRef(0);
  const processingIntervalRef = useRef(null);

  const getIdToken = useCallback(async () => {
    const user = auth.currentUser;
    return user ? await user.getIdToken() : null;
  }, [auth]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !userId || !competence?.id) return;

    // Validate file using the new utility function
    const validation = validateFile(file);

    if (!validation.isValid) {
      setError(validation.error);
      setSelectedFile(null);
      setFileInfo(null);
      setWarning(null);
      return;
    }

    // Set warning if present (e.g., large image)
    if (validation.warning) {
      setWarning(validation.warning);
    } else {
      setWarning(null);
    }

    // Get file info for display
    const info = getFileInfo(file);
    setFileInfo(info);

    // Get processing stages for this file type
    const stages = getProcessingStages(file);
    setProcessingSteps(stages);

    setSelectedFile(file);
    setError(null);
    // NE PAS appeler uploadFile automatiquement - attendre le clic sur le bouton
  }, [competence, userId]);

  const uploadFile = useCallback(async (file) => {
    if (!file || !userId || !competence?.id || loading) return;

    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);
      setUploadStatus('Préparation...');
      lastProgressRef.current = 0;

      // Nettoyer l'intervalle de traitement précédent
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }

      console.log('Téléchargement du fichier :', file.name, 'taille:', file.size, 'octets');

      // Get processing stages for this file type (used throughout upload)
      const stages = getProcessingStages(file);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify({
        title: file.name.replace(/\.[^/.]+$/, ""),
        documentType: file.name.split('.').pop().toUpperCase(),
        competenceId: competence.id,
        objectiveId: competence.id,
        uploadedAt: new Date().toISOString(),
        userId: userId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        source: 'upload',
        status: 'processing'
      }));

      const token = await getIdToken();
      if (!token) {
        throw new Error('Utilisateur non authentifié');
      }

      console.log("Front : Appel de la route /api/saveDocument avec suivi de progression");

      // PHASE 1 : Téléchargement (0-15%)
      setUploadStatus('Téléchargement du fichier...');

      const result = await uploadWithProgress(
        `${BackEndUrl}/api/saveDocument`,
        formData,
        token,
        (percent) => {
          // Limiter à 15% pour la phase de téléchargement
          const uploadProgress = Math.min(15, percent);

          // Garantir la monotonie : ne jamais revenir en arrière
          const monotonicProgress = Math.max(lastProgressRef.current, uploadProgress);
          lastProgressRef.current = monotonicProgress;

          setUploadProgress(monotonicProgress);

          // Mettre à jour le message en fonction de la progression
          setUploadStatus(getStatusMessage(monotonicProgress, stages));
        }
      );

      // PHASE 2 : Traitement estimé (15-100%)
      const estimatedTime = calculateProcessingTime(file);
      const processingDuration = estimatedTime * 1000; // en millisecondes
      const startTime = Date.now();
      const targetProgress = 100;
      const remainingProgress = targetProgress - 15;

      processingIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const estimatedProgress = 15 + (elapsed / processingDuration) * remainingProgress;

        if (estimatedProgress >= 100) {
          clearInterval(processingIntervalRef.current);
          processingIntervalRef.current = null;
          setUploadProgress(100);
          setUploadStatus('Terminé ! Document prêt.');

          // Traiter le résultat
          if (result.success && result.document) {
            setDocument(result.document);

            // Notifier le parent
            if (onUploadComplete) {
              onUploadComplete(result.document);
            }

            // Réinitialiser après un délai
            setTimeout(() => {
              setLoading(false);
              setSelectedFile(null);
              setFileInfo(null);
              setUploadProgress(0);
              setUploadStatus('');
            }, 2000);
          } else {
            throw new Error(result.message || 'Erreur lors du traitement du document');
          }
        } else {
          // Garantir la monotonie pendant le traitement
          const monotonicProgress = Math.max(lastProgressRef.current, Math.min(99, Math.round(estimatedProgress)));
          lastProgressRef.current = monotonicProgress;
          setUploadProgress(monotonicProgress);

          // Mettre à jour le message en fonction de la progression
          setUploadStatus(getStatusMessage(monotonicProgress, stages));
        }
      }, 100);

    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
      setError(`Erreur: ${err.message}`);
      setUploadStatus('Échec');
      
      // Réinitialiser après un délai
      setTimeout(() => {
        setLoading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 3000);
    }
  }, [userId, competence?.id, loading, getIdToken, onUploadComplete]);

  const handleUploadClick = useCallback(() => {
    if (selectedFile && !loading) {
      uploadFile(selectedFile);
    }
  }, [selectedFile, loading, uploadFile]);

  const handleRetry = useCallback(() => {
    if (selectedFile && !loading) {
      setError(null);
      uploadFile(selectedFile);
    }
  }, [selectedFile, loading, uploadFile]);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleCloseDocument = useCallback(() => {
    setDocument(null);
  }, []);

  // Réinitialiser l'input file après upload
  const resetFileInput = useCallback(() => {
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
    setSelectedFile(null);
    setError(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Zone de téléchargement */}
      <UploadZone
        onFileSelect={handleFileSelect}
        isProcessing={loading}
        disabled={!userId || !competence?.id}
        selectedFile={selectedFile}
        uploadProgress={uploadProgress}
        uploadStatus={uploadStatus}
        fileInfo={fileInfo}
      />

      {/* Warning display for large images */}
      {warning && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 mr-3 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-800 mb-1">Attention</h4>
              <p className="text-yellow-700 text-sm">{warning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Bouton d'upload */}
      {selectedFile && !loading && !error && (
        <div className="flex justify-center">
          <button
            onClick={handleUploadClick}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
            disabled={!selectedFile || loading}
          >
            <Upload className="w-5 h-5" />
            Uploader le document
          </button>
        </div>
      )}

      {/* Bouton de réinitialisation */}
      {selectedFile && !loading && (
        <div className="flex justify-center">
          <button
            onClick={resetFileInput}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
          >
            Sélectionner un autre fichier
          </button>
        </div>
      )}

      {/* Affichage d'erreur */}
      {error && (
        <ErrorDisplay
          error={error}
          onRetry={handleRetry}
          onClose={handleCloseError}
        />
      )}

      {/* Affichage du document après upload */}
      {document && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4 text-green-700 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Document téléchargé avec succès !
          </h3>
          <DocumentView
            document={document}
            onClose={handleCloseDocument}
          />
        </div>
      )}

      {/* Instructions */}
      {!selectedFile && !loading && !error && (
        <div className="text-sm text-gray-500 space-y-2">
          <p className="font-semibold text-gray-700">Formats supportés :</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            <div>
              <p className="font-medium text-gray-600">Documents</p>
              <p>• PDF, Word, PowerPoint, Excel</p>
            </div>
            <div>
              <p className="font-medium text-gray-600">Images</p>
              <p>• JPG, PNG, GIF, WebP, BMP, TIFF</p>
            </div>
            <div>
              <p className="font-medium text-gray-600">Texte</p>
              <p>• TXT, CSV, Markdown, RTF</p>
            </div>
          </div>
          <p className="mt-4">• Taille maximale : 50MB</p>
          <p>• Analyse automatique avec extraction des concepts</p>
          <p>• Analyse multimodale pour PowerPoint et images</p>
        </div>
      )}
    </div>
  );
};

export default UploadDocument;
